// src/utils/partnerHub.js
// Client for the partner_hub Lambda (backend/partner_hub): DMC trips, traveller ↔ partner chat
// mirrored to admin, call logs, push subscriptions, admin tools.
//
// Every call is POST { intent, auth, ...payload } to FEATURES.PARTNER_HUB_URL and returns
// { status, data, message } like the other RouteChef intents.
//
// Who is calling (auth):
//   traveller  { conv_id, conv_token }   handed out by conv_start, kept in localStorage per trip
//   partner    { partner_token, partner_from }   the partner session token from agentAuth.js;
//              the hub checks it against the orchestrator's agent_profile intent
//   admin      { admin_token }           Firebase ID token of a Google login on FEATURES.ADMIN_EMAILS
//
// With no URL and PARTNER_HUB_LOCAL_DEMO on, the same intents run against localStorage (demoHub
// below), so the whole flow can be tried in one browser. demoHub mirrors the Lambda's behaviour
// and doubles as its reference.
import { FEATURES } from "../config/features";
import { getAgentSession } from "./agentAuth";
import { auth as firebaseAuth } from "../firebase";

export const HUB_URL = FEATURES.PARTNER_HUB_URL || "";
export const HUB_DEMO = !HUB_URL && FEATURES.PARTNER_HUB_LOCAL_DEMO === true;
export const HUB_ON = !!HUB_URL || HUB_DEMO;
export const ADMIN_EMAILS = (FEATURES.ADMIN_EMAILS || []).map((e) => e.toLowerCase());
export const isAdminEmail = (email) => !!email && ADMIN_EMAILS.includes(String(email).toLowerCase());

const read = (k, d = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } };
const write = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage blocked */ } };

/* ── auth helpers ───────────────────────────────────────────────────────── */

export function partnerAuth() {
    const s = getAgentSession();
    return s ? { partner_token: s.token, partner_from: `webbot_agent:${s.profile.email}` } : null;
}

export const DEMO_ADMIN_KEY = "rc_demo_admin";
export async function adminAuth() {
    if (HUB_DEMO && read(DEMO_ADMIN_KEY) === 1) return { admin_token: "demo" };
    const u = firebaseAuth?.currentUser;
    if (!u || !isAdminEmail(u.email)) return null;
    try { return { admin_token: await u.getIdToken() }; } catch { return null; }
}

/* Traveller conversations, one per trip, remembered in this browser */
const CONV_KEY = (slug) => `rc_dmc_conv_${slug}`;
export const savedConv = (slug) => read(CONV_KEY(slug));
export const saveConv = (slug, conv) => write(CONV_KEY(slug), conv);
const CONTACT_KEY = "rc_dmc_contact";
export const savedContact = () => read(CONTACT_KEY, null);
export const saveContact = (c) => write(CONTACT_KEY, c);

/* ── transport ──────────────────────────────────────────────────────────── */

export async function hubCall(intent, payload = {}, authObj = null) {
    if (!HUB_ON) return { status: 503, data: null, message: "Partner hub isn't connected yet." };
    if (HUB_DEMO) {
        try { return await demoHub(intent, payload, authObj || {}); } catch (e) { return { status: 500, data: null, message: String(e?.message || e) }; }
    }
    try {
        const res = await fetch(HUB_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ intent, auth: authObj || {}, ...payload }),
        });
        const json = await res.json().catch(() => ({}));
        return { status: json?.status ?? res.status, data: json?.data ?? null, message: json?.message || "" };
    } catch {
        return { status: 0, data: null, message: "Network error. Check your connection and try again." };
    }
}

const asPartner = (intent, payload) => hubCall(intent, payload, partnerAuth());
const asAdmin = async (intent, payload) => hubCall(intent, payload, await adminAuth());

/* ── public / traveller ─────────────────────────────────────────────────── */

export const getDmcTrip = (partnerSlug, slug, { draft = false } = {}) =>
    draft ? asPartner("partner_trip_get", { slug }) : hubCall("dmc_trip_get", { partner_slug: partnerSlug, slug });
/* Saved versions of a trip: the partner (role "partner") or RouteChef (role "admin") names and saves a
   changed itinerary; anyone opens it at /dmc/<partner>/<trip>?plan=<plan_id> */
const asRole = (role) => (role === "admin" ? asAdmin : asPartner);
export const savePlan = (role, { tripSlug, name, state, summary, planId = "" }) =>
    asRole(role)("plan_save", { trip_slug: tripSlug, name, state, summary, ...(planId ? { plan_id: planId } : {}) });
export const listPlans = (role, tripSlug) => asRole(role)("plan_list", { trip_slug: tripSlug });
export const deletePlan = (role, planId) => asRole(role)("plan_delete", { plan_id: planId });
export const getPlan = async (planId) => hubCall("plan_get", { plan_id: planId }, partnerAuth() || (await adminAuth()) || {});
export const planUrl = (partnerSlug, tripSlug, planId) => `${window.location.origin}/dmc/${partnerSlug}/${tripSlug}?plan=${encodeURIComponent(planId)}`;
/* Rates live at once (no review): { tiers, vehicles, dmc_markup_pct, hide_item_prices }, plus markup_pct from admins only */
export const saveTripRates = (role, slug, rates) => asRole(role)("partner_trip_rates", { slug, ...rates });
/* Itinerary PDF (its text) → draft trip, read by Claude in the background. Poll the status. */
// Text PDFs: start with the text. Brochures without text: start with pages=N, send each page, then run.
export const startTripImport = (role, { text, filename, pages = 0 }) => asRole(role)("trip_import_start", { text, filename, pages });
export const sendImportPage = (role, jobId, n, data) => asRole(role)("trip_import_page", { job_id: jobId, n, data });
export const runTripImport = (role, jobId) => asRole(role)("trip_import_run", { job_id: jobId });
export const tripImportStatus = (role, jobId) => asRole(role)("trip_import_status", { job_id: jobId });
export const getStorefront = (partnerSlug) => hubCall("dmc_storefront", { partner_slug: partnerSlug });
/* An operator's claim link (sent with their WhatsApp lead alerts): what it covers, then take it over */
export const claimInfo = (token) => hubCall("claim_info", { token });
export const claimListing = (token, phone = "") => asPartner("partner_claim", { token, phone });
/* "List your trip" (/list-your-trip): anyone uploads an itinerary PDF without signing in and gets a private
   trip page (/p/<token>). The claim link goes to their email. lead = { company, email, phone, name } */
export const listTripStart = ({ lead, text, filename, pages = 0, captcha = "" }) => hubCall("list_trip_start", { ...lead, text, filename, pages, captcha });
export const listTripPage = (job, n, data) => hubCall("list_trip_page", { ...job, n, data });
export const listTripRun = (job) => hubCall("list_trip_run", job);
export const listTripStatus = (job) => hubCall("list_trip_status", job);
export const tripPreviewGet = (token) => hubCall("trip_preview_get", { token });
export const listTripResend = (token) => hubCall("list_trip_resend", { token });

export const startConversation = ({ trip, contact, snapshot }) => hubCall("conv_start", {
    trip_slug: trip.slug, trip_title: trip.title, partner_id: trip.partner?.agent_id, partner_slug: trip.partner?.slug,
    name: contact.name, phone: contact.phone, email: contact.email || "", snapshot,
});
export const sendAsTraveller = (conv, { text, kind = "text", snapshot = null }) =>
    hubCall("conv_send", { text, kind, snapshot }, { conv_id: conv.conv_id, conv_token: conv.conv_token });
export const pollAsTraveller = (conv, after = 0) =>
    hubCall("conv_poll", { after }, { conv_id: conv.conv_id, conv_token: conv.conv_token });
export const logCall = ({ trip, contact, conv, device }) => hubCall("call_log", {
    trip_slug: trip.slug, trip_title: trip.title, partner_id: trip.partner?.agent_id,
    name: contact?.name || "", phone: contact?.phone || "", device: device || "",
}, conv ? { conv_id: conv.conv_id, conv_token: conv.conv_token } : {});

/* ── partner ────────────────────────────────────────────────────────────── */

export const partnerInbox = (after = 0) => asPartner("partner_inbox", { after });
export const partnerConversation = (convId, after = 0) => asPartner("partner_conv", { conv_id: convId, after });
// extra: { kind: "plan", snapshot } sends the traveller a changed version of their trip
export const partnerSend = (convId, text, extra = {}) => asPartner("partner_send", { conv_id: convId, text, ...extra });
export const partnerRead = (convId) => asPartner("partner_read", { conv_id: convId });
export const partnerCalls = () => asPartner("partner_calls", {});
export const partnerTrips = () => asPartner("partner_trips", {});
export const partnerTripGet = (slug) => asPartner("partner_trip_get", { slug });
export const partnerTripSave = (trip) => asPartner("partner_trip_save", { trip });
export const partnerTripSubmit = (slug) => asPartner("partner_trip_submit", { slug });
export const partnerUnread = () => asPartner("unread_counts", { role: "partner" });

/* ── admin ──────────────────────────────────────────────────────────────── */

export const adminInbox = (filter = {}) => asAdmin("admin_inbox", filter);
export const adminConversation = (convId, after = 0) => asAdmin("admin_conv", { conv_id: convId, after });
export const adminSend = (convId, text, extra = {}) => asAdmin("admin_send", { conv_id: convId, text, ...extra });
export const adminRead = (convId) => asAdmin("admin_read", { conv_id: convId });
export const adminCalls = () => asAdmin("admin_calls", {});
export const adminTrips = () => asAdmin("admin_trips", {});
/* One-time WhatsApp claim invite for a trip RouteChef made: { link, message, sent, can_send, phone, expires_at } */
export const adminTripInvite = (slug, phone, send) => asAdmin("admin_trip_invite", { slug, phone, send: !!send });
/* Cover photos: n upload links { put_url, url } (partner for their trips, or admin) */
export const photoUploadLinks = (role, slug, n) => (role === "admin" ? asAdmin : asPartner)("photo_upload", { slug: slug || "", n });
export const adminTripReview = (slug, decision, extra = {}) => asAdmin("admin_trip_review", { slug, decision, ...extra });
export const adminTripGet = (slug) => asAdmin("admin_trip_get", { slug });
// owner_email can belong to someone who hasn't signed up yet: the trip moves to them on first sign-in
export const adminTripSave = (trip, { ownerEmail = "", publish = false, isNew = false, markupPct } = {}) =>
    asAdmin("admin_trip_save", { trip, owner_email: ownerEmail, publish, is_new: isNew, ...(markupPct != null && markupPct !== "" ? { markup_pct: Number(markupPct) } : {}) });
export const adminTripAssign = (slug, ownerEmail) => asAdmin("admin_trip_assign", { slug, owner_email: ownerEmail });
export const adminPartners = () => asAdmin("admin_partners", {});
export const adminCreatePartner = (form) => asAdmin("admin_create_partner", form);
export const adminUnread = () => asAdmin("unread_counts", { role: "admin" });

/* ── push ───────────────────────────────────────────────────────────────── */

export async function subscribePush(role, subscription, convAuth = null) {
    const a = role === "admin" ? await adminAuth() : role === "partner" ? partnerAuth() : convAuth;
    return hubCall("push_subscribe", { role, subscription }, a);
}

/* ════════════════════════════════════════════════════════════════════════
   demoHub: the Lambda's behaviour on localStorage, for trying the flow without a backend
   ════════════════════════════════════════════════════════════════════════ */

const DEMO_KEY = "rc_hub_demo_v1";
export const DEMO_EVENT = "rc-hub-demo-change";
const demoDb = () => read(DEMO_KEY, null) || { convs: {}, msgs: {}, calls: [], trips: {}, partners: [], seq: 0 };
const demoSave = (db) => { write(DEMO_KEY, db); try { window.dispatchEvent(new Event(DEMO_EVENT)); } catch { /* no window */ } };
const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const ok = (data) => ({ status: 200, data, message: "" });
const err = (status, message) => ({ status, data: null, message });

function demoWho(a) {
    if (a.admin_token) return { role: "admin", id: "admin" };
    if (a.partner_token) {
        const s = getAgentSession();
        return s && s.token === a.partner_token ? { role: "partner", id: s.profile.user_id, profile: s.profile } : null;
    }
    return null;
}

const convView = (c) => ({ ...c, conv_token: undefined });

async function demoHub(intent, p, a) {
    const db = demoDb();
    const now = Date.now();
    const who = demoWho(a);
    const travellerConv = () => {
        const c = db.convs[a.conv_id];
        return c && c.conv_token === a.conv_token ? c : null;
    };
    const addMsg = (c, m) => {
        db.seq += 1;
        const msg = { id: rid("m"), seq: db.seq, conv_id: c.conv_id, at: now, ...m };
        (db.msgs[c.conv_id] = db.msgs[c.conv_id] || []).push(msg);
        c.last_at = now; c.last_text = m.text; c.last_from = m.from; c.seq = db.seq;
        if (m.from === "user") { c.unread_partner += 1; c.unread_admin += 1; }
        if (m.from === "partner") { c.unread_user += 1; c.unread_admin += 1; c.partner_replied_at = c.partner_replied_at || now; }
        if (m.from === "admin") { c.unread_user += 1; c.unread_partner += 1; }
        if (m.from === "system") { c.unread_partner += 1; c.unread_admin += 1; }
        return msg;
    };
    const after = (list, n) => (list || []).filter((m) => m.seq > (Number(n) || 0));

    switch (intent) {
        case "dmc_trip_get": {
            const t = Object.values(db.trips).find((x) => x.slug === p.slug && x.status === "published" && (!p.partner_slug || x.partner?.slug === p.partner_slug));
            return t ? ok({ trip: t.published || t }) : err(404, "Not found");
        }
        case "dmc_storefront": {
            const list = Object.values(db.trips).filter((x) => x.partner?.slug === p.partner_slug && x.status === "published").map((x) => x.published || x);
            return list.length ? ok({ partner: list[0].partner, trips: list }) : err(404, "Not found");
        }
        case "conv_start": {
            if (!p.name || !/^\+?\d[\d ]{9,14}$/.test(String(p.phone || "").trim())) return err(400, "Name and a valid phone number are needed.");
            const c = {
                conv_id: rid("c"), conv_token: rid("t"), trip_slug: p.trip_slug, trip_title: p.trip_title, partner_id: p.partner_id, partner_slug: p.partner_slug || "",
                user: { name: p.name, phone: p.phone, email: p.email || "" }, snapshot: p.snapshot || null,
                created_at: now, last_at: now, last_text: "", unread_partner: 0, unread_admin: 0, unread_user: 0, seq: 0,
            };
            db.convs[c.conv_id] = c;
            addMsg(c, { from: "system", kind: "start", text: `${p.name} started a chat about ${p.trip_title}`, snapshot: p.snapshot || null });
            demoSave(db);
            return ok({ conv_id: c.conv_id, conv_token: c.conv_token });
        }
        case "conv_send": {
            const c = travellerConv(); if (!c) return err(401, "Chat expired. Start again.");
            if (!String(p.text || "").trim()) return err(400, "Empty message");
            if (p.snapshot) c.snapshot = p.snapshot;
            const m = addMsg(c, { from: "user", name: c.user.name, kind: p.kind || "text", text: String(p.text).slice(0, 2000), snapshot: p.kind !== "text" ? p.snapshot : null });
            demoSave(db);
            return ok({ message: m });
        }
        case "conv_poll": {
            const c = travellerConv(); if (!c) return err(401, "Chat expired. Start again.");
            const msgs = after(db.msgs[c.conv_id], p.after);
            if (c.unread_user) { c.unread_user = 0; demoSave(db); }
            return ok({ messages: msgs, partner_seen: c.partner_seen_at || null });
        }
        case "call_log": {
            const c = a.conv_id ? travellerConv() : null;
            const entry = { id: rid("call"), at: now, trip_slug: p.trip_slug, trip_title: p.trip_title, partner_id: p.partner_id, name: p.name || c?.user?.name || "Unknown", phone: p.phone || c?.user?.phone || "", device: p.device || "", conv_id: c?.conv_id || null };
            db.calls.unshift(entry);
            if (c) addMsg(c, { from: "system", kind: "call", text: `${entry.name} tapped Call` });
            demoSave(db);
            return ok({ call: entry });
        }
        case "push_subscribe":
            return ok({ saved: true });

        case "unread_counts": {
            if (!who) return err(401, "Sign in again");
            const mine = Object.values(db.convs).filter((c) => who.role === "admin" || c.partner_id === who.id);
            const key = who.role === "admin" ? "unread_admin" : "unread_partner";
            const latest = mine.sort((x, y) => y.last_at - x.last_at)[0];
            return ok({ unread: mine.reduce((s, c) => s + (c[key] || 0), 0), conversations: mine.filter((c) => c[key]).length, latest: latest ? convView(latest) : null, calls: db.calls.filter((x) => who.role === "admin" || x.partner_id === who.id).length });
        }
        case "partner_inbox": case "admin_inbox": {
            if (!who || (intent === "admin_inbox" && who.role !== "admin")) return err(401, "Sign in again");
            let list = Object.values(db.convs).filter((c) => who.role === "admin" || c.partner_id === who.id);
            if (p.partner_id) list = list.filter((c) => c.partner_id === p.partner_id);
            return ok({ conversations: list.sort((x, y) => y.last_at - x.last_at).map(convView) });
        }
        case "partner_conv": case "admin_conv": {
            if (!who) return err(401, "Sign in again");
            const c = db.convs[p.conv_id];
            if (!c || (who.role === "partner" && c.partner_id !== who.id)) return err(404, "Not found");
            return ok({ conversation: convView(c), messages: after(db.msgs[c.conv_id], p.after) });
        }
        case "partner_send": case "admin_send": {
            if (!who) return err(401, "Sign in again");
            const c = db.convs[p.conv_id];
            if (!c || (who.role === "partner" && c.partner_id !== who.id)) return err(404, "Not found");
            if (!String(p.text || "").trim()) return err(400, "Empty message");
            const plan = p.kind === "plan" && p.snapshot?.state ? { kind: "plan", snapshot: p.snapshot } : { kind: "text" };
            const m = addMsg(c, who.role === "admin"
                ? { from: "admin", name: "RouteChef", text: String(p.text).slice(0, 2000), ...plan }
                : { from: "partner", name: who.profile?.business_name || who.profile?.name || who.id, text: String(p.text).slice(0, 2000), ...plan });
            if (who.role === "partner") c.unread_partner = 0; else c.unread_admin = 0;
            demoSave(db);
            return ok({ message: m });
        }
        case "partner_read": case "admin_read": {
            if (!who) return err(401, "Sign in again");
            const c = db.convs[p.conv_id];
            if (!c) return err(404, "Not found");
            if (who.role === "admin") c.unread_admin = 0; else { c.unread_partner = 0; c.partner_seen_at = now; }
            demoSave(db);
            return ok({ read: true });
        }
        case "partner_calls": case "admin_calls": {
            if (!who) return err(401, "Sign in again");
            return ok({ calls: db.calls.filter((x) => who.role === "admin" || x.partner_id === who.id) });
        }
        case "partner_trips": {
            if (!who) return err(401, "Sign in again");
            return ok({ trips: Object.values(db.trips).filter((t) => t.partner?.agent_id === who.id) });
        }
        case "partner_trip_get": {
            if (!who) return err(401, "Sign in again");
            const t = db.trips[p.slug];
            if (!t || (who.role === "partner" && t.partner?.agent_id !== who.id)) return err(404, "Not found");
            return ok({ trip: t });
        }
        case "partner_trip_save": {
            if (!who) return err(401, "Sign in again");
            const t = p.trip;
            if (!t?.slug) return err(400, "Trip needs a link name");
            const cur = db.trips[t.slug];
            if (cur && who.role === "partner" && cur.partner?.agent_id !== who.id) return err(409, "That link name is taken");
            const partner = who.role === "partner"
                ? { ...(t.partner || {}), agent_id: who.id, name: t.partner?.name || who.profile?.business_name || who.profile?.name, phone: t.partner?.phone || who.profile?.phone || "", public_ref: who.profile?.public_ref || "" }
                : t.partner;
            // Published trips keep serving the approved copy until the edit is approved
            const next = { ...t, partner, markup_pct: cur?.markup_pct ?? t.markup_pct ?? 10, status: cur?.status === "published" ? "published" : "draft", draft_changed: cur?.status === "published", updated_at: now, published: cur?.published || null, review_note: cur?.review_note || "" };
            db.trips[t.slug] = next;
            demoSave(db);
            return ok({ trip: next });
        }
        case "partner_trip_submit": {
            if (!who) return err(401, "Sign in again");
            const t = db.trips[p.slug];
            if (!t || t.partner?.agent_id !== who.id) return err(404, "Not found");
            t.review = "pending"; t.submitted_at = now;
            if (t.status !== "published") t.status = "pending";
            demoSave(db);
            return ok({ trip: t });
        }
        case "admin_trip_get": {
            if (who?.role !== "admin") return err(401, "Admins only");
            return db.trips[p.slug] ? ok({ trip: db.trips[p.slug] }) : err(404, "Not found");
        }
        case "admin_trip_save": case "admin_trip_assign": {
            if (who?.role !== "admin") return err(401, "Admins only");
            const cur = db.trips[p.slug || p.trip?.slug];
            if (intent === "admin_trip_save" && p.is_new && cur) return err(409, "That link name is taken");
            const email = String(p.owner_email || cur?.owner_email || "").trim().toLowerCase();
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return err(400, "Enter the owner's email");
            const owner = `invite:${email}`;
            const t = intent === "admin_trip_save" ? p.trip : cur;
            if (!t) return err(404, "Not found");
            const next = { ...t, partner: { ...(t.partner || {}), agent_id: owner }, owner_email: email, owner_pending: true, markup_pct: cur?.markup_pct ?? 10, updated_at: now,
                status: p.publish || cur?.status === "published" ? "published" : "draft", draft_changed: !p.publish && cur?.status === "published", review: p.publish ? "approved" : cur?.review || "" };
            db.trips[next.slug] = next;
            demoSave(db);
            return ok({ trip: next, owner_signed_up: false });
        }
        case "partner_trip_rates": {
            if (!who) return err(401, "Sign in again");
            const t = db.trips[p.slug];
            if (!t || (who.role === "partner" && t.partner?.agent_id !== who.id)) return err(404, "Not found");
            // Rates go live at once: the draft and the published copy
            for (const doc of [t, t.published].filter(Boolean)) {
                if (p.tiers) doc.tiers = p.tiers;
                if (p.vehicles) doc.cab = { ...(doc.cab || {}), vehicles: p.vehicles };
                if (p.dmc_markup_pct != null) doc.dmc_markup_pct = Number(p.dmc_markup_pct) || 0;
                if (typeof p.hide_item_prices === "boolean") doc.hide_item_prices = p.hide_item_prices;
                if (p.markup_pct != null && who.role === "admin") doc.markup_pct = Number(p.markup_pct);
            }
            demoSave(db);
            return ok({ trip: t.published || t, live: t.status === "published" });
        }
        case "admin_trip_invite": case "photo_upload":
            return err(501, "The demo hub can't send WhatsApp invites or store photos. Connect the partner hub.");
        case "list_trip_start": case "list_trip_page": case "list_trip_run": case "list_trip_status": case "trip_preview_get": case "list_trip_resend":
            return { status: 501, data: null, message: "Making trip pages from a PDF needs the live partner hub." };
        case "trip_import_start": case "trip_import_status": case "trip_import_page": case "trip_import_run":
            return err(501, "The demo hub can't read itineraries. Connect the partner hub to use PDF import.");
        case "plan_save": {
            if (!who) return err(401, "Sign in again");
            db.plans = db.plans || {};
            const key = String(p.name || "").trim().toLowerCase().replace(/\s+/g, " ");
            if (key.length < 2) return err(400, "Give this version a name");
            const mine = Object.values(db.plans).filter((x) => x.trip_slug === p.trip_slug);
            if (mine.some((x) => x.name_key === key && x.plan_id !== p.plan_id)) return err(409, `There's already a version called "${String(p.name).trim()}" on this trip. Pick another name.`);
            const cur = p.plan_id ? db.plans[p.plan_id] : null;
            const { plan_from, version, ...st } = p.state || {}; // eslint-disable-line no-unused-vars
            const it = { ...(cur || { plan_id: `v${rid("").slice(-8)}`, trip_slug: p.trip_slug, created_at: now }), name: String(p.name).trim(), name_key: key, state: st, summary: p.summary || cur?.summary || {}, updated_at: now };
            db.plans[it.plan_id] = it;
            demoSave(db);
            const { state: _s, ...pub } = it; // eslint-disable-line no-unused-vars
            return ok({ plan: pub });
        }
        case "plan_get": {
            const it = (db.plans || {})[p.plan_id];
            return it ? ok({ plan: it }) : err(404, "This version link doesn't work any more. Here's the trip as the partner offers it.");
        }
        case "plan_list": {
            if (!who) return err(401, "Sign in again");
            return ok({ plans: Object.values(db.plans || {}).filter((x) => x.trip_slug === p.trip_slug).sort((a, b) => b.updated_at - a.updated_at).map(({ state, ...x }) => x) }); // eslint-disable-line no-unused-vars
        }
        case "plan_delete": {
            if (!who) return err(401, "Sign in again");
            if (db.plans) delete db.plans[p.plan_id];
            demoSave(db);
            return ok({});
        }
        case "admin_trips": {
            if (who?.role !== "admin") return err(401, "Admins only");
            return ok({ trips: Object.values(db.trips).sort((x, y) => (y.submitted_at || 0) - (x.submitted_at || 0)) });
        }
        case "admin_trip_review": {
            if (who?.role !== "admin") return err(401, "Admins only");
            const t = db.trips[p.slug];
            if (!t) return err(404, "Not found");
            if (p.markup_pct != null) { t.markup_pct = Number(p.markup_pct); if (t.published) t.published.markup_pct = t.markup_pct; }
            if (p.decision === "approve") {
                const copy = { ...t }; delete copy.published;
                t.status = "published"; t.review = "approved"; t.draft_changed = false; t.published = { ...copy, status: "published", markup_pct: t.markup_pct }; t.review_note = p.note || "";
            } else if (p.decision === "reject") {
                t.review = "changes_requested"; t.review_note = p.note || ""; if (t.status !== "published") t.status = "draft";
            } else if (p.decision === "unpublish") {
                t.status = "draft"; t.review = "unpublished";
            }
            demoSave(db);
            return ok({ trip: t });
        }
        case "admin_partners": {
            if (who?.role !== "admin") return err(401, "Admins only");
            return ok({ partners: db.partners });
        }
        case "admin_create_partner": {
            if (who?.role !== "admin") return err(401, "Admins only");
            const email = String(p.email || "").trim().toLowerCase();
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return err(400, "Enter a valid email");
            if (db.partners.some((x) => x.email === email)) return err(409, "A partner with this email already exists");
            const entry = { user_id: email.split("@")[0], email, name: p.name, business_name: p.business_name, phone: p.phone, city: p.city || "", kind: p.kind || "dmc", region: p.region || "", must_change_password: true, created_at: now, created_by: "admin" };
            db.partners.unshift(entry);
            demoSave(db);
            return ok({ partner: entry });
        }
        default:
            return err(400, `Unknown intent ${intent}`);
    }
}

/** Demo only: put a trip into the demo hub as published (used to seed the Kashmir trip) */
export function demoSeedTrip(trip) {
    if (!HUB_DEMO) return;
    const db = demoDb();
    if (db.trips[trip.slug]) return;
    db.trips[trip.slug] = { ...trip, status: "published", review: "approved", published: { ...trip, status: "published" }, updated_at: Date.now() };
    demoSave(db);
}
